import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';

import { FullBuyContext, TFullBuyContextProperties } from '~/src/features/full-buy/analytics/context';
import useOldAddressFormNextButton from '~/src/features/full-buy/containers/GoToNextStepButton/useOldAddressFormNextButton';
import usePrivateSectorEquipmentNextButton from '~/src/features/full-buy/containers/GoToNextStepButton/usePrivateSectorEquipmentNextButton';
import getStepForNextButton from '~/src/features/full-buy/store/selectors/nextButton/getStepForNextButton';
import { ESteps } from '~/src/features/full-buy/types';

import useContactsNextButton from './useContactsNextButton';
import useEquipmentsNextButton from './useEquipmentsNextButton';
import useEquipmentsTvNextButton from './useEquipmentsTvNextButton';
import useStubNextButton from './useStubNextButton';
import useSubscriptionsNextButton from './useSubscriptionsNextButton';
import useTariffInfoNextButton from './useTariffInfoNextButton';
import useUnifiedAddressFormNextButton from './useUnifiedAddressFormNextButton';

const hooksList = {
    [ESteps.tariffInfo]: useTariffInfoNextButton,
    [ESteps.subscriptions]: useSubscriptionsNextButton,
    [ESteps.equipments]: useEquipmentsNextButton,
    [ESteps.equipmentsTv]: useEquipmentsTvNextButton,
    [ESteps.equipmentsCottage]: usePrivateSectorEquipmentNextButton,
    /**
     * We have a two address input screens by one AB test (one-line input, old input).
     * The AB test returns a boolean value, so in this case for the step we have added `_${Number(boolean)}` variable
     * to identify which screen is being and which hook should be used.
     */
    [`${ESteps.address}_1`]: useUnifiedAddressFormNextButton,
    [`${ESteps.address}_0`]: useOldAddressFormNextButton,
    [ESteps.contacts]: useContactsNextButton,
    [ESteps.agreementCreated]: useStubNextButton,
    [ESteps.timeSlots]: useStubNextButton,
    [ESteps.final]: useStubNextButton,
};

const GoToNextStepButton = (): JSX.Element | null => {
    const { isVillage } = useContext<TFullBuyContextProperties>(FullBuyContext);
    const step = useSelector(getStepForNextButton(isVillage));
    const useDynamicHook = useMemo(() => {
        return (step && step in hooksList) ? hooksList[step] : useStubNextButton;
    }, [step]);

    const { children, doNotDisplay = false, ...props } = useDynamicHook();

    if (doNotDisplay) {
        return null;
    }

    return <ButtonGroup>
        <Button {...props}>
            {children}
        </Button>
    </ButtonGroup>;
};

export default GoToNextStepButton;
